<script setup lang="ts">
import type { BlogPost } from "~/types/blog";

definePageMeta({
  layout: "default",
});

const route = useRoute();
const config = useRuntimeConfig();
const siteOrigin = config.public.mainUrl as string;
const slug = computed(() => String(route.params.slug ?? "").toLowerCase());

const { data, error } = await useFetch<{ post: BlogPost }>(() => `/api/blog/${slug.value}`);

// An unknown slug must be a real 404, not a 200 with an empty body — otherwise
// every mistyped URL gets indexed as a thin page. `fatal` renders the error
// page during SSR with the right status code.
if (error.value) {
  throw createError({
    statusCode: error.value.statusCode === 404 ? 404 : 502,
    statusMessage: error.value.statusCode === 404 ? "Post not found" : "Could not load this post",
    fatal: true,
  });
}

const post = computed(() => data.value?.post ?? null);

// The body is rendered with `v-html` — the only way to render article markup.
// It is allowlist-sanitised server-side in `server/utils/sanitiseHtml.ts` before
// it ever reaches this payload. Never bind unsanitised feed content there.
const canonical = computed(() => `${siteOrigin}/blog/${slug.value}`);

const { formatPostDate } = useBlogFormat();

// Every field below falls back to `undefined`, never `""`. An empty string still
// renders the tag — `<meta property="article:section" content>` — which is worse
// than omitting it, and for the fields that also exist in `nuxt.config`'s global
// head (description, og:image, og:image:alt) an empty override would *blank the
// site default* rather than fall back to it.
const seoImage = computed(() => post.value?.image || undefined);
// Only describe the image when it is the post's own; otherwise the global
// og:image and its matching global alt must stay paired.
const seoImageAlt = computed(() => (post.value?.image ? post.value.title : undefined));

useSeoMeta({
  title: () => (post.value ? `${post.value.title} | &Dine` : "Blog | &Dine"),
  description: () => post.value?.excerpt || undefined,
  ogType: "article",
  // og:url must be the canonical, not the requested URL — otherwise a shared
  // link carrying `?utm_source=…` is treated as a separate object by crawlers
  // and social caches.
  ogUrl: canonical,
  ogTitle: () => post.value?.title || undefined,
  ogDescription: () => post.value?.excerpt || undefined,
  ogImage: seoImage,
  ogImageAlt: seoImageAlt,
  articlePublishedTime: () => post.value?.publishedAt || undefined,
  // RSS carries no "updated" field, so published time is the only modification
  // signal we have. Stating it explicitly beats leaving crawlers to guess.
  articleModifiedTime: () => post.value?.publishedAt || undefined,
  articleSection: () => post.value?.categories?.[0] || undefined,
  twitterTitle: () => post.value?.title || undefined,
  twitterDescription: () => post.value?.excerpt || undefined,
  twitterImage: seoImage,
  twitterImageAlt: seoImageAlt,
});

useHead({
  link: [{ rel: "canonical", href: canonical }],
});

useSchemaGraph("ld-blog-post", () => {
  const current = post.value;
  if (!current) return [];

  return [
    {
      "@type": "BlogPosting",
      "@id": `${canonical.value}#article`,
      headline: current.title,
      description: current.excerpt,
      url: canonical.value,
      // `dateModified` mirrors `datePublished` because the feed exposes no
      // updated timestamp. Google treats a missing dateModified as unknown
      // freshness, which is worse than an accurate "never edited since publish".
      ...(current.publishedAt
        ? { datePublished: current.publishedAt, dateModified: current.publishedAt }
        : {}),
      ...(current.image ? { image: current.image } : {}),
      ...(current.categories.length ? { articleSection: current.categories } : {}),
      // Fall back to the Organization when the feed names no author — an
      // article with no author at all is a Google Search Console warning.
      author: current.author
        ? { "@type": "Person", name: current.author }
        : { "@id": `${siteOrigin}/#organization` },
      isPartOf: { "@id": `${siteOrigin}/blog#blog` },
      publisher: { "@id": `${siteOrigin}/#organization` },
      mainEntityOfPage: { "@type": "WebPage", "@id": canonical.value },
    },
    {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: siteOrigin },
        { "@type": "ListItem", position: 2, name: "Blog", item: `${siteOrigin}/blog` },
        { "@type": "ListItem", position: 3, name: current.title, item: canonical.value },
      ],
    },
  ];
});
</script>

<template>
  <article
    v-if="post"
    class="post"
  >
    <div class="post__inner">
      <NuxtLink
        class="post__back"
        to="/blog"
      >
        <fa :icon="['fal', 'arrow-left']" />
        <span>All posts</span>
      </NuxtLink>

      <header class="post__header">
        <p
          v-if="post.categories.length"
          class="post__categories"
        >
          {{ post.categories.join(" · ") }}
        </p>

        <h1 class="post__title">
          {{ post.title }}
        </h1>

        <p class="post__meta">
          <time
            v-if="post.publishedAt"
            :datetime="post.publishedAt"
          >
            {{ formatPostDate(post.publishedAt) }}
          </time>
          <span
            v-if="post.publishedAt"
            aria-hidden="true"
          >·</span>
          <span v-if="post.author">{{ post.author }}</span>
          <span
            v-if="post.author"
            aria-hidden="true"
          >·</span>
          <span>{{ post.readingMinutes }} min read</span>
        </p>
      </header>

      <img
        v-if="post.image"
        :alt="post.title"
        class="post__hero"
        decoding="async"
        height="675"
        :src="post.image"
        width="1200"
      >

      <!-- eslint-disable-next-line vue/no-v-html -->
      <div
        v-if="post.content"
        class="post__body"
        v-html="post.content"
      />

      <footer class="post__footer">
        <p>Hungry for something to feed the team?</p>
        <DynamicButton
          color="pink"
          text="Browse Makers"
          @click-event="navigateTo('/search')"
        />
      </footer>
    </div>
  </article>
</template>

<style lang="scss" scoped>
.post {
  padding-top: 5rem; // clears the fixed NavBar

  &__inner {
    max-width: 46rem;
    margin: 0 auto;
    padding: var(--space-5) 5% var(--space-8);
  }

  &__back {
    display: inline-flex;
    align-items: center;
    gap: var(--space-2);
    margin-bottom: var(--space-4);
    color: var(--color-grey-2);
    font-size: var(--text-sm);
    text-decoration: none;

    &:hover {
      color: var(--color-pink-1);
    }
  }

  &__categories {
    margin-bottom: var(--space-2);
    color: var(--color-grey-2);
    font-size: var(--text-xs);
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }

  &__title {
    font-size: var(--text-4xl);
    font-weight: var(--weight-semibold);
    line-height: 1.2;

    @media (max-width: $mobile) {
      font-size: var(--text-2xl);
    }
  }

  &__meta {
    display: flex;
    flex-wrap: wrap;
    gap: var(--space-1);
    margin-top: var(--space-3);
    color: var(--color-grey-2);
    font-size: var(--text-sm);
  }

  &__hero {
    display: block;
    width: 100%;
    height: auto;
    margin: var(--space-5) 0;
    border-radius: var(--radius-lg);
  }

  &__footer {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
    align-items: flex-start;
    margin-top: var(--space-7);
    padding-top: var(--space-5);
    border-top: 1px solid var(--color-grey-3);
  }
}

/*
 * Article body styling. Not scoped-deep by choice — `v-html` content carries no
 * scope attribute, so `:deep()` is the only selector that reaches it.
 */
.post__body {
  margin-top: var(--space-5);
  color: var(--color-grey-1);
  font-size: var(--text-base);
  line-height: 1.75;

  :deep(h2) {
    margin: var(--space-6) 0 var(--space-3);
    font-size: var(--text-2xl);
    font-weight: var(--weight-semibold);
  }

  :deep(h3) {
    margin: var(--space-5) 0 var(--space-2);
    font-size: var(--text-xl);
    font-weight: var(--weight-semibold);
  }

  :deep(h4) {
    margin: var(--space-4) 0 var(--space-2);
    font-size: var(--text-lg);
    font-weight: var(--weight-semibold);
  }

  :deep(p) {
    margin-bottom: var(--space-4);
  }

  :deep(ul),
  :deep(ol) {
    margin: 0 0 var(--space-4) var(--space-4);
  }

  :deep(li) {
    margin-bottom: var(--space-2);
  }

  :deep(a) {
    color: var(--color-grey-1);
    font-weight: var(--weight-semibold);
    text-decoration: underline;

    &:hover {
      color: var(--color-pink-1);
    }
  }

  :deep(blockquote) {
    margin: var(--space-5) 0;
    padding-left: var(--space-4);
    border-left: 0.25rem solid var(--color-grey-3);
    color: var(--color-grey-2);
    font-style: italic;
  }

  :deep(img) {
    display: block;
    width: 100%;
    height: auto;
    margin: var(--space-4) 0;
    border-radius: var(--radius);
  }

  :deep(figcaption) {
    color: var(--color-grey-2);
    font-size: var(--text-sm);
    text-align: center;
  }

  :deep(pre) {
    margin-bottom: var(--space-4);
    padding: var(--space-3);
    overflow-x: auto;
    background: var(--color-grey-4);
    border-radius: var(--radius-sm);
    font-size: var(--text-sm);
  }

  :deep(code) {
    background: var(--color-grey-4);
    border-radius: var(--radius-sm);
    padding: 0 var(--space-1);
    font-size: var(--text-sm);
  }

  :deep(hr) {
    margin: var(--space-6) 0;
    border: 0;
    border-top: 1px solid var(--color-grey-3);
  }

  /* Wide tables must scroll inside the article, never widen the page. */
  :deep(table) {
    display: block;
    width: 100%;
    margin-bottom: var(--space-4);
    overflow-x: auto;
    border-collapse: collapse;
  }

  :deep(th),
  :deep(td) {
    padding: var(--space-2);
    border: 1px solid var(--color-grey-3);
    text-align: left;
  }
}
</style>
